import { useState, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Star, ChevronRight, Disc3, Layers, BookOpen, Flag } from "lucide-react";
import { TuneTypeBadge } from "./TuneTypeBadge";
import { AbcNotation } from "./AbcNotation";
import { buildPreviewAbc } from "../lib/abcUtils";
import { toggleFavourite, removeFavourite, favKey, favouritesForTune } from "../lib/favourites";
import { topFlagForTune, flagHex } from "../lib/flags";

const Stat = ({ icon, value, title }) => (
  <span
    className="inline-flex items-center gap-1 text-[11px] font-bold text-[#6e655b]"
    title={title}
  >
    {icon}
    {value}
  </span>
);

export const TuneCard = memo(({ tune, index = 0 }) => {
  const navigate = useNavigate();
  // saved state is per version: surface the newest favourite + one flag
  const [saved, setSaved] = useState({ fav: null, flag: null });

  useEffect(() => {
    const update = () =>
      setSaved({
        fav: favouritesForTune(tune.tune_id)[0] || null,
        flag: topFlagForTune(tune.tune_id),
      });
    update();
    window.addEventListener("flag-changed", update);
    window.addEventListener("fav-changed", update);
    return () => {
      window.removeEventListener("flag-changed", update);
      window.removeEventListener("fav-changed", update);
    };
  }, [tune.tune_id]);

  // the flag wins as the version to reopen, then the favourite
  const target = saved.flag || saved.fav;
  const showVersion = target && (target.version > 1 || target.edited);

  const openTune = () =>
    navigate(
      `/tune/${tune.tune_id}${
        target ? `?fav=${encodeURIComponent(target.key)}` : tune.matched_version > 1 ? `?v=${tune.matched_version}` : ""
      }`
    );

  const onFav = (e) => {
    e.stopPropagation();
    if (saved.fav) {
      removeFavourite(saved.fav.key);
      return;
    }
    const previewAbc = buildPreviewAbc({
      key: tune.preview_key,
      meter: tune.preview_meter,
      abc: tune.preview_abc,
    });
    toggleFavourite({
      key: favKey(tune.tune_id, 1, false),
      tune_id: tune.tune_id,
      name: tune.name,
      type: tune.type,
      version: 1,
      edited: false,
      abc: previewAbc,
    });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openTune}
      onKeyDown={(e) => e.key === "Enter" && openTune()}
      className="group float-up cursor-pointer rounded-3xl border border-[#e5e0d8] bg-[#fffdf9] p-5 shadow-[0_8px_30px_rgba(45,40,36,0.05)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(45,40,36,0.10)]"
      style={{ animationDelay: `${Math.min(index * 45, 400)}ms` }}
      data-testid="tune-card"
    >
      <div className="flex items-start justify-between gap-3">
        <h3
          className="font-serif text-2xl font-bold leading-tight text-[#2d2824]"
          data-testid="tune-card-title"
        >
          {tune.name}
        </h3>
        <div className="flex shrink-0 items-center gap-2">
          {saved.flag && (
            <Flag
              className="h-5 w-5"
              style={{ color: flagHex(saved.flag.color), fill: flagHex(saved.flag.color) }}
              data-testid="tune-card-flag"
              data-flag-color={saved.flag.color}
            />
          )}
          <button
            onClick={onFav}
            className="transition-transform duration-200 active:scale-125"
            data-testid="favourite-toggle"
            aria-label="favourite"
          >
            <Star
              className="h-6 w-6"
              strokeWidth={2}
              style={{
                color: saved.fav ? "#4CAF50" : "#c9c1b5",
                fill: saved.fav ? "#4CAF50" : "none",
              }}
            />
          </button>
          <ChevronRight className="h-6 w-6 text-[#a89e92] transition-transform duration-200 group-hover:translate-x-1" />
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <TuneTypeBadge type={tune.type} />
        {showVersion && (
          <span
            className="rounded-full bg-[#2d2824] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white"
            data-testid="tune-card-saved-version"
          >
            v{target.version}
            {target.edited ? " · edited" : ""}
          </span>
        )}
        <Stat
          icon={<Disc3 className="h-3.5 w-3.5" />}
          value={tune.recordings_count}
          title="recordings"
        />
        <Stat
          icon={<BookOpen className="h-3.5 w-3.5" />}
          value={tune.tunebooks}
          title="tunebooks"
        />
        <Stat
          icon={<Layers className="h-3.5 w-3.5" />}
          value={tune.versions_count}
          title="versions"
        />
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl bg-[#fbf9f4] px-3 py-3">
        <AbcNotation
          abc={buildPreviewAbc({
            key: tune.preview_key,
            meter: tune.preview_meter,
            abc: tune.preview_abc,
          })}
          params={{ preferredMeasuresPerLine: 4 }}
        />
      </div>
    </div>
  );
});
TuneCard.displayName = "TuneCard";
